import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IconHistory, IconPlus } from '@rossum/ui/icons/tabler';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useChatHistory } from '../hooks/useChatHistory';
import { ChatHistoryItem } from '../types';

type ChatHistoryDropdownProps = {
  onSelectChat: (chatId: string) => void;
  onCreateNew: () => void;
  currentChatId: string | null;
  activeChatIds: Set<string>;
  unseenChatIds: Set<string>;
  renderTrigger?: (
    onClick: (event: React.MouseEvent<HTMLElement>) => void
  ) => React.ReactNode;
};

const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MAX_TITLE_LENGTH = 50;
const MAX_PREVIEW_LENGTH = 80;

const truncate = (text: string, maxLength: number): string =>
  text.length <= maxLength ? text : `${text.slice(0, maxLength)}…`;

const useGetChatTitle = () => {
  const intl = useIntl();
  return (chat: ChatHistoryItem) => {
    if (chat.summary) {
      return chat.summary;
    }
    if (chat.first_message) {
      return truncate(chat.first_message, MAX_TITLE_LENGTH);
    }
    return intl.formatMessage(
      { id: 'features.chat.history.chatFrom' },
      { timestamp: formatTimestamp(chat.timestamp) }
    );
  };
};

const getChatPreview = (chat: ChatHistoryItem): string | null => {
  if (!chat.preview) return null;
  return truncate(chat.preview, MAX_PREVIEW_LENGTH);
};

export const ChatHistoryDropdown = ({
  onSelectChat,
  onCreateNew,
  currentChatId,
  activeChatIds,
  unseenChatIds,
  renderTrigger,
}: ChatHistoryDropdownProps) => {
  const intl = useIntl();
  const getChatTitle = useGetChatTitle();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const { data: chats, isLoading } = useChatHistory({
    enabled: isOpen,
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectChat = (chatId: string) => {
    onSelectChat(chatId);
    handleClose();
  };

  const handleCreateNewChat = () => {
    onCreateNew();
    handleClose();
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <IconButton onClick={handleOpen} size="small">
          <SvgIcon fontSize="medium">
            <IconHistory />
          </SvgIcon>
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 400,
              maxHeight: 400,
            },
          },
        }}
      >
        <MenuItem
          data-cy="chat-new-chat-button"
          onClick={handleCreateNewChat}
          sx={{ gap: 1 }}
          selected={currentChatId === null}
        >
          <ListItemIcon>
            <SvgIcon>
              <IconPlus />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>
            {intl.formatMessage({ id: 'features.chat.history.newChat' })}
          </ListItemText>
        </MenuItem>

        <Divider />

        {isLoading ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2">
              {intl.formatMessage({ id: 'features.chat.history.loading' })}
            </Typography>
          </MenuItem>
        ) : !chats?.length ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: 'features.chat.history.empty' })}
            </Typography>
          </MenuItem>
        ) : (
          chats.map(chat => {
            const preview = getChatPreview(chat);
            return (
              <MenuItem
                key={chat.chat_id}
                data-cy="chat-history-item"
                onClick={
                  chat.chat_id !== currentChatId
                    ? () => handleSelectChat(chat.chat_id)
                    : undefined
                }
                selected={chat.chat_id === currentChatId}
                sx={{ gap: 1 }}
              >
                <Box
                  data-cy="chat-history-unseen-dot"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: borderRadius('xl'),
                    bgcolor: 'warning.main',
                    flexShrink: 0,
                    // Always takes its slot so titles line up across rows.
                    visibility: unseenChatIds.has(chat.chat_id)
                      ? 'visible'
                      : 'hidden',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {getChatTitle(chat)}
                  </Typography>
                  {preview && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: 'block' }}
                    >
                      {preview}
                    </Typography>
                  )}
                </Box>
                {activeChatIds.has(chat.chat_id) && (
                  <CircularProgress size={14} sx={{ flexShrink: 0 }} />
                )}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};
